import { useState, useMemo, useCallback } from "react";
import { Copy, Check, ArrowLeftRight, Sun, Moon, Sparkles, RotateCcw } from "lucide-react";
import { useSeoMeta } from "@unhead/react";

import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { useToast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";

import { OPERATIONS, OPERATION_MAP, compute } from "@/lib/bitwise";
import type { BitwiseOperation } from "@/lib/bitwise";
import { FORMAT_MAP, encodeAll } from "@/lib/bitcodecs";
import type { InputFormatId, InputFormat } from "@/lib/bitcodecs";
import { BitVisualizer } from "./BitVisualizer";

// ─── operation button ──────────────────────────────────────────────────

function OperationButton({
  op,
  selected,
  onClick,
}: {
  op: BitwiseOperation;
  selected: boolean;
  onClick: () => void;
}) {
  const isXor = op.id === "xor";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-2.5 transition-all",
        "min-w-[72px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
        selected
          ? "border-foreground/20 bg-foreground/5 dark:bg-foreground/10 shadow-sm"
          : "border-border/60 hover:border-foreground/15 hover:bg-foreground/[0.03] dark:hover:bg-foreground/[0.06]",
        isXor && !selected && "border-violet-300/50 dark:border-violet-700/40",
      )}
    >
      <span
        className={cn(
          "font-mono text-lg font-bold leading-none transition-colors",
          selected
            ? "text-foreground"
            : isXor
              ? "text-violet-600 dark:text-violet-400"
              : "text-muted-foreground group-hover:text-foreground",
        )}
      >
        {op.symbol}
      </span>
      <span
        className={cn(
          "text-[10px] leading-tight font-medium transition-colors text-center",
          selected ? "text-foreground" : "text-muted-foreground/70",
        )}
      >
        {op.name}
      </span>
      {isXor && (
        <span className="absolute -top-1.5 -right-1.5 flex size-3.5 items-center justify-center rounded-full bg-violet-500 text-white">
          <Sparkles className="size-2" />
        </span>
      )}
    </button>
  );
}

// ─── color swatch ─────────────────────────────────────────────────────

function bytesToHexColor(bytes: Uint8Array): string | null {
  if (bytes.length !== 3) return null;
  return `#${Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("")}`;
}

function ColorSwatch({ bytes, size = "md" }: { bytes: Uint8Array; size?: "sm" | "md" | "lg" }) {
  const hex = bytesToHexColor(bytes);
  if (!hex) return null;

  const sizeClass = size === "lg" ? "h-16" : size === "sm" ? "h-8" : "h-12";

  return (
    <div
      className={`${sizeClass} w-full rounded-md border border-border/40 transition-colors`}
      style={{ backgroundColor: hex }}
      role="img"
      aria-label={`Color ${hex}`}
    />
  );
}

// ─── input panel ───────────────────────────────────────────────────────

interface InputPanelProps {
  label: "A" | "B";
  color: "sky" | "emerald";
  format: InputFormatId;
  value: string;
  error: string | null;
  bytes: Uint8Array | null;
  onFormatChange: (fmt: InputFormatId) => void;
  onValueChange: (val: string) => void;
  disabled?: boolean;
  /** When true, render a shift-amount number input instead of format+textarea */
  shiftMode?: boolean;
  shiftValue?: string;
  onShiftValueChange?: (val: string) => void;
}

const COLOR_ACCENT = {
  sky: {
    badge: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20",
    dot: "bg-sky-500",
  },
  emerald: {
    badge: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
    dot: "bg-emerald-500",
  },
};

function InputPanel({
  label,
  color,
  format,
  value,
  error,
  bytes,
  onFormatChange,
  onValueChange,
  disabled,
  shiftMode,
  shiftValue,
  onShiftValueChange,
}: InputPanelProps) {
  const accent = COLOR_ACCENT[color];
  const byteCount = bytes?.length ?? 0;
  const bitCount = byteCount * 8;

  return (
    <Card className="gap-0 overflow-hidden">
      <CardHeader className="gap-3 pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={cn("size-2.5 rounded-full", accent.dot)} />
            <span className="text-sm font-semibold">Input {label}</span>
          </div>
          {!shiftMode && (
            <Select value={format} onValueChange={(v) => onFormatChange(v as InputFormatId)} disabled={disabled}>
              <SelectTrigger size="sm" className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>General</SelectLabel>
                  <SelectItem value="hex">Hex</SelectItem>
                  <SelectItem value="binary">Binary</SelectItem>
                  <SelectItem value="utf8">UTF-8 Text</SelectItem>
                  <SelectItem value="base64">Base64</SelectItem>
                  <SelectItem value="decimal">Decimal</SelectItem>
                  <SelectItem value="bytes">Raw Bytes</SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Crypto</SelectLabel>
                  <SelectItem value="nsec">Nostr nsec</SelectItem>
                  <SelectItem value="wif">Bitcoin WIF</SelectItem>
                  <SelectItem value="mnemonic">BIP-39 Mnemonic</SelectItem>
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Visual</SelectLabel>
                  <SelectItem value="rgb">RGB Color</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </div>
      </CardHeader>
      <CardContent className="gap-0">
        {shiftMode ? (
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">
              Shift amount (bits)
            </label>
            <Input
              type="number"
              min={0}
              value={shiftValue ?? ""}
              onChange={(e) => onShiftValueChange?.(e.target.value)}
              placeholder="e.g. 8"
              className="font-mono"
            />
          </div>
        ) : (
          <Textarea
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={FORMAT_MAP[format]?.hint ?? "Enter value…"}
            disabled={disabled}
            className="min-h-[80px] font-mono text-sm leading-relaxed"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
          />
        )}
        {format === "rgb" && bytes && bytes.length === 3 && !shiftMode && (
          <div className="mt-2 flex items-center gap-3">
            <ColorSwatch bytes={bytes} size="sm" />
            <span className="text-xs font-mono text-muted-foreground">
              {bytesToHexColor(bytes)}
            </span>
          </div>
        )}
        <div className="mt-2 flex items-center justify-between">
          {error ? (
            <span className="text-xs text-destructive">{error}</span>
          ) : !shiftMode && byteCount > 0 ? (
            <span className="text-xs text-muted-foreground">
              {byteCount} {byteCount === 1 ? "byte" : "bytes"} · {bitCount} bits
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/50">—</span>
          )}
          {!shiftMode && byteCount > 0 && (
            <Badge variant="outline" className={cn("text-[10px] font-mono", accent.badge)}>
              {format.toUpperCase()}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── output row ────────────────────────────────────────────────────────

function OutputRow({
  label,
  value,
  monospace = true,
}: {
  label: string;
  value: string;
  monospace?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: "Copy failed", description: "Clipboard access denied" });
    }
  }, [value, toast]);

  return (
    <div className="group flex items-start gap-3 border-b border-border/40 py-2 last:border-0">
      <span className="w-24 shrink-0 pt-0.5 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "text-sm break-all leading-relaxed",
            monospace && "font-mono",
          )}
        >
          {value}
        </p>
      </div>
      <Button
        size="icon-xs"
        variant="ghost"
        onClick={handleCopy}
        className="mt-0.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        aria-label={`Copy ${label}`}
      >
        {copied ? (
          <Check className="size-3 text-emerald-500" />
        ) : (
          <Copy className="size-3" />
        )}
      </Button>
    </div>
  );
}

// ─── presets ───────────────────────────────────────────────────────────

interface Preset {
  label: string;
  opId: string;
  aFormat: InputFormatId;
  aValue: string;
  bFormat: InputFormatId;
  bValue: string;
}

const PRESETS: Preset[] = [
  {
    label: "XOR two hex values",
    opId: "xor",
    aFormat: "hex",
    aValue: "deadbeefcafebabe",
    bFormat: "hex",
    bValue: "0123456789abcdef",
  },
  {
    label: "XOR text with key",
    opId: "xor",
    aFormat: "utf8",
    aValue: "secret message",
    bFormat: "hex",
    bValue: "a5c3d1e4f2b09876a5c3d1e4f2b09876",
  },
  {
    label: "XOR two RGB colors",
    opId: "xor",
    aFormat: "rgb",
    aValue: "#ff0088",
    bFormat: "rgb",
    bValue: "#00ffff",
  },
  {
    label: "XOR two 32-byte keys",
    opId: "xor",
    aFormat: "hex",
    aValue: "0102030405060708090a0b0c0d0e0f101112131415161718191a1b1c1d1e1f20",
    bFormat: "hex",
    bValue: "ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff",
  },
  {
    label: "AND two binary values",
    opId: "and",
    aFormat: "binary",
    aValue: "11110000 10101010",
    bFormat: "binary",
    bValue: "11001100 11110000",
  },
  {
    label: "Left rotate by 4",
    opId: "left-rotate",
    aFormat: "hex",
    aValue: "12345678",
    bFormat: "decimal",
    bValue: "4",
  },
  {
    label: "XOR a BIP-39 mnemonic",
    opId: "xor",
    aFormat: "mnemonic",
    aValue: "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about",
    bFormat: "hex",
    bValue: "00000000000000000000000000000001",
  },
];

// ─── main component ────────────────────────────────────────────────────

export function BitwiseTool() {
  useSeoMeta({
    title: "XOR Anything — Bitwise Operation Tool",
    description:
      "XOR, AND, OR, and 13 other bitwise operations on hex, binary, text, Base64, Nostr nsec, Bitcoin WIF, BIP-39 mnemonics, RGB colors, and more.",
  });

  const { theme, setTheme } = useTheme();

  const [opId, setOpId] = useState("xor");
  const [aFormat, setAFormat] = useState<InputFormatId>("hex");
  const [aValue, setAValue] = useState("deadbeefcafebabe");
  const [bFormat, setBFormat] = useState<InputFormatId>("hex");
  const [bValue, setBValue] = useState("0123456789abcdef");
  const [shiftAmount, setShiftAmount] = useState("8");

  const op = OPERATION_MAP[opId];
  const isShiftOp = op?.category === "shift";

  // Decode inputs
  const { aBytes, aError } = useMemo(() => {
    try {
      const fmt: InputFormat | undefined = FORMAT_MAP[aFormat];
      if (!fmt) return { aBytes: null, aError: null };
      if (!aValue.trim()) return { aBytes: new Uint8Array(0), aError: null };
      return { aBytes: fmt.decode(aValue), aError: null };
    } catch (e) {
      return { aBytes: null, aError: e instanceof Error ? e.message : "Decode error" };
    }
  }, [aFormat, aValue]);

  const { bBytes, bError } = useMemo(() => {
    if (isShiftOp) return { bBytes: null, bError: null };
    try {
      const fmt: InputFormat | undefined = FORMAT_MAP[bFormat];
      if (!fmt) return { bBytes: null, bError: null };
      if (!bValue.trim()) return { bBytes: new Uint8Array(0), bError: null };
      return { bBytes: fmt.decode(bValue), bError: null };
    } catch (e) {
      return { bBytes: null, bError: e instanceof Error ? e.message : "Decode error" };
    }
  }, [bFormat, bValue, isShiftOp]);

  // Compute result
  const resultBytes = useMemo(() => {
    if (aError) return null;
    if (!isShiftOp && bError) return null;
    const a = aBytes;
    if (!a) return null;

    if (isShiftOp) {
      const n = parseInt(shiftAmount, 10);
      if (isNaN(n) || n < 0) return null;
      // Pack shift amount as 4-byte big-endian for compute()
      const shiftAsBytes = new Uint8Array(4);
      new DataView(shiftAsBytes.buffer).setUint32(0, Math.min(n, 0xffffffff), false);
      try {
        return compute(opId, a, shiftAsBytes);
      } catch {
        return null;
      }
    }

    const b = bBytes;
    if (!b) return null;
    try {
      return compute(opId, a, b);
    } catch {
      return null;
    }
  }, [opId, aBytes, bBytes, aError, bError, isShiftOp, shiftAmount]);

  // Encode outputs
  const outputs = useMemo(() => {
    if (!resultBytes || resultBytes.length === 0) return [];
    return encodeAll(resultBytes);
  }, [resultBytes]);

  // Shift amount as bytes for the visualizer
  const shiftBytes = useMemo(() => {
    if (!isShiftOp) return new Uint8Array(0);
    const n = parseInt(shiftAmount, 10);
    if (isNaN(n)) return new Uint8Array(0);
    // Represent as 4-byte big-endian
    const bytes = new Uint8Array(4);
    new DataView(bytes.buffer).setUint32(0, Math.min(n, 0xffffffff), false);
    return bytes;
  }, [isShiftOp, shiftAmount]);

  const handleSwap = useCallback(() => {
    setAFormat(bFormat);
    setAValue(bValue);
    setBFormat(aFormat);
    setBValue(aValue);
  }, [aFormat, aValue, bFormat, bValue]);

  const handleReset = useCallback(() => {
    setOpId("xor");
    setAFormat("hex");
    setAValue("deadbeefcafebabe");
    setBFormat("hex");
    setBValue("0123456789abcdef");
    setShiftAmount("8");
  }, []);

  const applyPreset = useCallback((preset: Preset) => {
    setOpId(preset.opId);
    setAFormat(preset.aFormat);
    setAValue(preset.aValue);
    setBFormat(preset.bFormat);
    setBValue(preset.bValue);
  }, []);

  // Group operations by category for display
  const booleanOps = OPERATIONS.filter((o) => o.category === "binary");
  const unaryOps = OPERATIONS.filter((o) => o.category === "unary");
  const shiftOps = OPERATIONS.filter((o) => o.category === "shift");

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* ─── header ──────────────────────────────────────────── */}
        <header className="border-b border-border/60 bg-gradient-to-b from-violet-500/[0.04] to-transparent">
          <div className="container mx-auto max-w-5xl px-4 py-6 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent dark:from-violet-400 dark:to-fuchsia-400">
                    XOR
                  </span>{" "}
                  Anything
                </h1>
                <p className="mt-1 text-sm text-muted-foreground sm:text-base">
                  16 bitwise operations on hex, binary, text, Base64, Nostr nsec, Bitcoin WIF, BIP-39 mnemonics, RGB colors & more
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                  aria-label="Toggle theme"
                >
                  {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
          {/* ─── operation selector ────────────────────────────── */}
          <Card className="gap-0">
            <CardHeader className="gap-3 pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm">Operation</CardTitle>
                <Button size="xs" variant="ghost" onClick={handleReset}>
                  <RotateCcw className="size-3" /> Reset
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Boolean ops */}
              <div>
                <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
                  Boolean — 10 ops
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {booleanOps.map((o) => (
                    <OperationButton
                      key={o.id}
                      op={o}
                      selected={opId === o.id}
                      onClick={() => setOpId(o.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Unary ops */}
              <div>
                <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
                  Unary — 2 ops
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {unaryOps.map((o) => (
                    <OperationButton
                      key={o.id}
                      op={o}
                      selected={opId === o.id}
                      onClick={() => setOpId(o.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Shift ops */}
              <div>
                <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/60">
                  Shift & Rotate — 4 ops
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {shiftOps.map((o) => (
                    <OperationButton
                      key={o.id}
                      op={o}
                      selected={opId === o.id}
                      onClick={() => setOpId(o.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Description */}
              {op && (
                <p className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  <span className="font-mono font-semibold text-foreground">{op.symbol}</span>{" "}
                  — {op.description}
                </p>
              )}
            </CardContent>
          </Card>

          {/* ─── presets ───────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Try:</span>
            {PRESETS.map((preset) => (
              <Button
                key={preset.label}
                size="xs"
                variant="outline"
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          {/* ─── inputs ────────────────────────────────────────── */}
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <InputPanel
                label="A"
                color="sky"
                format={aFormat}
                value={aValue}
                error={aError}
                bytes={aBytes}
                onFormatChange={setAFormat}
                onValueChange={setAValue}
                disabled={op?.category === "unary" && opId === "not-b"}
              />

              {isShiftOp ? (
                <InputPanel
                  label="B"
                  color="emerald"
                  format={bFormat}
                  value={bValue}
                  error={null}
                  bytes={null}
                  onFormatChange={setBFormat}
                  onValueChange={setBValue}
                  shiftMode
                  shiftValue={shiftAmount}
                  onShiftValueChange={setShiftAmount}
                />
              ) : (
                <InputPanel
                  label="B"
                  color="emerald"
                  format={bFormat}
                  value={bValue}
                  error={bError}
                  bytes={bBytes}
                  onFormatChange={setBFormat}
                  onValueChange={setBValue}
                  disabled={op?.category === "unary" && opId === "not-a"}
                />
              )}
            </div>

            {!isShiftOp && op?.category !== "unary" && (
              <div className="flex justify-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="outline" onClick={handleSwap}>
                      <ArrowLeftRight className="size-3.5" /> Swap A ↔ B
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Swap inputs A and B</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>

          {/* ─── result ────────────────────────────────────────── */}
          <Card className="border-amber-500/20 bg-amber-500/[0.02] dark:bg-amber-500/[0.04]">
            <CardHeader className="gap-3 pb-2">
              <div className="flex items-center gap-2">
                <span className="size-2.5 rounded-full bg-amber-500" />
                <CardTitle className="text-sm">Result</CardTitle>
                {resultBytes && resultBytes.length > 0 && (
                  <Badge variant="outline" className="text-[10px] font-mono">
                    {resultBytes.length} {resultBytes.length === 1 ? "byte" : "bytes"} · {resultBytes.length * 8} bits
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {resultBytes && resultBytes.length > 0 ? (
                <>
                  {/* Color preview strip — shown when A, B (if applicable), and result are all 3-byte RGB colors */}
                  {(() => {
                    const aHex = bytesToHexColor(aBytes ?? new Uint8Array(0));
                    const bHex = isShiftOp ? null : bytesToHexColor(bBytes ?? new Uint8Array(0));
                    const rHex = bytesToHexColor(resultBytes);
                    if (!rHex) return null;
                    // Show when result is RGB and at least input A is also RGB
                    if (!aHex) return null;

                    const swatches: { label: string; bytes: Uint8Array; hex: string }[] = [
                      { label: "A", bytes: aBytes!, hex: aHex },
                    ];
                    if (bHex) swatches.push({ label: "B", bytes: bBytes!, hex: bHex });
                    swatches.push({ label: "Result", bytes: resultBytes, hex: rHex });

                    return (
                      <div className="mb-4 flex items-end gap-2 rounded-lg border border-border/40 bg-muted/20 p-4 sm:gap-4">
                        {swatches.map((s, i) => (
                          <div key={s.label} className="contents">
                            <div className="flex flex-1 flex-col items-center gap-1.5">
                              <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
                              <ColorSwatch bytes={s.bytes} size="lg" />
                              <span className="text-xs font-mono text-muted-foreground">{s.hex}</span>
                            </div>
                            {i < swatches.length - 1 && (
                              <div className="flex items-center pb-8">
                                <span className="text-lg font-bold text-muted-foreground/60">{op?.symbol}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  <div className="space-y-0">
                    {outputs.map((out) => (
                      <OutputRow
                        key={out.formatId}
                        label={out.label}
                        value={out.value}
                        monospace={out.formatId !== "utf8" && out.formatId !== "mnemonic"}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    {aError || bError
                      ? "Fix the input errors to see the result."
                      : "Enter values in A and B to compute the result."}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ─── bit visualization ─────────────────────────────── */}
          {resultBytes && resultBytes.length > 0 && (
            <Card>
              <CardHeader className="gap-2 pb-3">
                <CardTitle className="text-sm">Bit Visualization</CardTitle>
                <p className="text-xs text-muted-foreground">
                  First 128 bits (16 bytes) — 1 bits are filled, 0 bits are empty.
                  {resultBytes.length > 16 && ` Showing first 16 of ${resultBytes.length} bytes.`}
                </p>
              </CardHeader>
              <CardContent>
                <BitVisualizer
                  a={aBytes ?? new Uint8Array(0)}
                  b={isShiftOp ? shiftBytes : (bBytes ?? new Uint8Array(0))}
                  result={resultBytes}
                  bIsShiftAmount={isShiftOp}
                />
                <div className="mt-4 flex flex-wrap gap-4">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="size-3 rounded-sm bg-sky-500" /> Input A
                  </span>
                  {!isShiftOp && (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="size-3 rounded-sm bg-emerald-500" /> Input B
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="size-3 rounded-sm bg-amber-500" /> Result
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ─── footer ────────────────────────────────────────── */}
          <footer className="border-t border-border/60 pt-6 text-center">
            <p className="text-xs text-muted-foreground">
              All operations run locally in your browser. No data leaves your device.{" "}
              <a
                href="https://marlowe.shakespeare.wtf"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-violet-600 underline-offset-2 hover:underline dark:text-violet-400"
              >
                Vibed with Marlowe
              </a>
            </p>
          </footer>
        </main>
      </div>
    </TooltipProvider>
  );
}
